<script setup lang="ts">
import ModalContainer from "@/components/modal/modal-container.vue";
import ModalTitle from "@/components/modal/modal-title.vue";
import ModalContent from "@/components/modal/modal-content.vue";
import { computed, ref, toRef } from 'vue'
import { faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { format } from "date-fns/format";
import { DateFormats } from "@/helpers/formatting";
import ModalFooter from "@/components/modal/modal-footer.vue";
import type { InvoiceActivity } from "@/api/billing/schemas";
import ButtonSecondary from "@/components/button-secondary.vue";
import DataTable from "@/components/data-table.vue";
import DataTableCell from "@/components/data-table-cell.vue";
import DataTableHeader from "@/components/data-table-header.vue";
import HeroSplashError from "@/components/hero-splash-error.vue";
import ContentLoader from "@/components/content-loader.vue";
import useUserList from "@/composables/users/user-list";
import { useAuthenticatedUser } from '@/shared/composables/useAuthenticatedUser'
import { useDebounce } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { fetchActivityLogQueryOptions } from '@/features/invoices/queries'
import InputText from '@/components/input-text.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = defineProps<{ invoiceId: number, title: string }>()
defineEmits<{ 'close': [] }>()

const formatDate = (date: Date) => format(date, DateFormats.UserFriendly)
const search = ref('')
const searchDebounced = useDebounce(search, 500)

const { data: authenticatedUser, isLoading: isLoadingAuthenticatedUser } = useAuthenticatedUser()

const {
  data: activityLog,
  isFetching: isFetchingActivityLog,
  isError: activityLogHasError
} = useQuery(
  fetchActivityLogQueryOptions(() => props.invoiceId)
)

const filteredActivityLog = computed(
  () => activityLog.value?.filter(
    x => (
      x.activity.toLowerCase().includes(searchDebounced.value.toLowerCase()) ||
      x.userId?.toLowerCase().includes(searchDebounced.value.toLowerCase()) ||
      formatDate(x.date).toLowerCase().includes(searchDebounced.value.toLowerCase()) ||
      x.oldValue?.toLowerCase().includes(searchDebounced.value.toLowerCase()) ||
      x.newValue?.toLowerCase().includes(searchDebounced.value.toLowerCase())
    )
  ) ?? []
)

const {
  data: userList,
  isFetching: isFetchingUserList,
  isError: userListHasError
} = useUserList(() => authenticatedUser.value?.organisationId)

const userListAsMap = computed(
  () => (userList.value || []).reduce(
    (accumulation, current) => {
      accumulation[current.id] = current.name

      return accumulation
    },
    { } as Record<string, string>
  )
)

const isFetching = computed(
  () => isFetchingUserList.value || isFetchingActivityLog.value || isLoadingAuthenticatedUser.value
)

const isError = computed(
  () => activityLogHasError.value || userListHasError.value
)
</script>

<template>
  <ModalContainer :width="950">
    <ModalTitle class="d-flex justify-content-between align-items-center">
      <span>Activity - {{ title }}</span>
      <InputText v-model:value="search">
        <template #suffix><FontAwesomeIcon :icon="faSearch" /></template>
      </InputText>
    </ModalTitle>
    <ModalContent>
      <ContentLoader :loading="isFetching" variant="table">
        <HeroSplashError v-if="isError" />

        <DataTable v-else :records="filteredActivityLog">
          <template #headers>
            <DataTableHeader>Activity</DataTableHeader>
            <DataTableHeader>Old</DataTableHeader>
            <DataTableHeader>New</DataTableHeader>
            <DataTableHeader>User</DataTableHeader>
            <DataTableHeader>Date</DataTableHeader>
          </template>

          <template #record="{ record }">
            <DataTableCell>{{ record.activity }}</DataTableCell>
            <DataTableCell>{{ record.oldValue ?? '-' }}</DataTableCell>
            <DataTableCell>{{ record.newValue ?? '-' }}</DataTableCell>
            <DataTableCell>{{ record.userId ? userListAsMap[record.userId] || record.userId : 'Unknown' }}</DataTableCell>
            <DataTableCell>{{ format(record.date, DateFormats.UserFriendly) }}</DataTableCell>
          </template>
        </DataTable>
      </ContentLoader>
    </ModalContent>
    <ModalFooter class="text-end">
      <ButtonSecondary @click="$emit('close')" :icon="faTimes">Close</ButtonSecondary>
    </ModalFooter>
  </ModalContainer>
</template>

<style scoped lang="scss">

</style>
